import { Application, Container, Graphics } from "pixi.js";
import { DEMO_FORM, type SearchStimulus, searchWrongTapBudget } from "@talent/tasks";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, mix, touchMin } from "./paint.js";

/**
 * Find them all — a field of arrows and a sign saying which way.
 *
 * The child taps every arrow pointing the way the sign does, and the trial ends when they have all
 * been found or the wrong-tap budget runs out. It is the one activity here that is not over when
 * something is found, so what it records is how a child covers ground rather than whether they can
 * recognise one thing.
 *
 * **A found arrow stays marked.** Without that the child has to remember where they have already
 * tapped, which turns a search into a memory task — there is a game for that and this is not it.
 * The remaining count sits at the bottom as pips, for the same reason spot-the-difference has them:
 * a numeral would be text on a surface that may not contain any (v2 §15), and without some counter
 * a child cannot tell "nearly done" from "just started".
 *
 * **Wrong taps end the trial rather than costing a point.** The budget scales with the field, so a
 * child sweeping twenty cells is forgiven more than one sweeping nine. Something has to bound it or
 * tapping every cell solves every trial.
 */

export interface SearchTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface Attempt {
  found: number[];
  wrongTaps: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => SearchStimulus;
  scoreTrial: (
    stimulus: SearchStimulus,
    found: readonly number[],
    wrongTaps: number,
  ) => boolean;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  startAt?: number;
}

/**
 * One arrow, pointing in one of eight directions.
 *
 * Drawn from a polygon rotated about the centre rather than from eight hand-written paths: eight
 * paths would be eight chances for one direction to end up subtly fatter than its neighbours, and
 * on the near-miss levels the child is being asked to tell a quarter turn apart. They have to be
 * the same arrow.
 */
function drawArrow(g: Graphics, direction: number, size: number, color: number): void {
  const angle = (direction * Math.PI) / 4;
  const r = size * 0.34;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // Local coordinates with +y pointing along the arrow, then rotated. Direction 0 is up, so the
  // tip starts at -y and the rotation runs clockwise, matching the generator's numbering.
  const at = (x: number, y: number): [number, number] => [
    x * cos - y * sin,
    x * sin + y * cos,
  ];

  const shaft = r * 0.3;
  const head = r * 0.62;
  const points: [number, number][] = [
    [0, -r],
    [head, -r + head * 1.15],
    [shaft, -r + head * 1.15],
    [shaft, r],
    [-shaft, r],
    [-shaft, -r + head * 1.15],
    [-head, -r + head * 1.15],
  ];
  g.poly(points.flatMap(([x, y]) => at(x, y))).fill(color);
}

export async function runSearchTask(opts: RunOptions): Promise<SearchTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.18, Math.min(w, h) * 0.42).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(stimulus: SearchStimulus): {
    attempt: Promise<Attempt>;
    simulateTap: (cell: number) => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const sign = new Graphics();
    const field = new Container();
    const pips = new Graphics();
    layer.addChild(sign, field, pips);

    const targetSet = new Set(stimulus.targets);
    const found = new Set<number>();
    /**
     * Targets the GAME showed at the end of a spent budget, kept apart from the ones the child
     * found.
     *
     * They have to be a separate set, and the first version learnt that the hard way: it added them
     * to `found` and filtered them out again on the way to the record, but the filter kept anything
     * in `targetSet` — which every revealed cell is. So `trial_response.response` claimed the child
     * had found arrows they never touched, on exactly the trials where they had run out of budget.
     * A record that overstates what a child did is worse than no record.
     */
    const revealed = new Set<number>();
    let wrongTaps = 0;
    let accepting = true;
    let resolveAttempt: ((a: Attempt) => void) | null = null;
    const budget = searchWrongTapBudget(stimulus);

    let tiles: Graphics[] = [];
    let cellSize = touchMin();

    function drawTile(cell: number): void {
      const g = tiles[cell];
      if (g === undefined) return;
      // Marked the same either way: a child looking at the end of a trial should see every arrow
      // that matched, not a puzzle about which ones were theirs.
      const hit = found.has(cell) || revealed.has(cell);
      g.clear();
      g.roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.24).fill(
        hit ? mix(COLOR.correct, COLOR.stage, 0.5) : mix(COLOR.stage, COLOR.card, 0.12),
      );
      if (hit) {
        g.roundRect(-cellSize / 2, -cellSize / 2, cellSize, cellSize, cellSize * 0.24).stroke({
          width: 4,
          color: COLOR.correct,
        });
      }
      drawArrow(g, stimulus.cells[cell] ?? 0, cellSize, hit ? COLOR.correct : COLOR.cream);
    }

    /** How many are left, as pips. A numeral would be text (v2 §15). */
    function drawPips(): void {
      const total = stimulus.targets.length;
      const y = app.renderer.height - 30;
      const step = 20;
      let x = (app.renderer.width - (total - 1) * step) / 2;
      pips.clear();
      for (let i = 0; i < total; i++) {
        const done = i < found.size;
        pips.circle(x, y, done ? 7.5 : 6).fill({
          color: done ? COLOR.correct : COLOR.card,
          alpha: done ? 1 : 0.24,
        });
        x += step;
      }
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const { cols, rows } = stimulus;

      const gap = Math.max(6, w * 0.018);
      // The back control floats over the top of the canvas and the pips sit along the bottom.
      const topReserved = 58;
      const bottomReserved = 50;
      /** The sign, and the space around it. Part of the budget, not added afterwards. */
      const signSize = Math.min(w * 0.2, 74);
      const signBlock = signSize + gap * 3;

      const usableH = h - topReserved - bottomReserved - signBlock;
      const byWidth = (w * 0.94 - (cols - 1) * gap) / cols;
      const byHeight = (usableH - (rows - 1) * gap) / rows;
      cellSize = Math.max(28, Math.min(byWidth, byHeight));

      const fieldW = cellSize * cols + gap * (cols - 1);
      const fieldH = cellSize * rows + gap * (rows - 1);
      const signY = topReserved + signSize / 2;
      const originX = (w - fieldW) / 2 + cellSize / 2;
      const originY = topReserved + signBlock + (usableH - fieldH) / 2 + cellSize / 2;

      /**
       * The sign: the direction being hunted for, on a card of its own.
       *
       * Deliberately not the same shape as a field tile. A tile-shaped sign at the top of a grid
       * reads as part of the grid — as a cell that has somehow escaped — and the first thing a
       * child would do is tap it.
       */
      sign.clear();
      sign
        .circle(w / 2, signY, signSize / 2)
        .fill(mix(COLOR.stage, COLOR.card, 0.2))
        .stroke({ width: 3, color: accent, alpha: 0.8 });
      const marker = new Graphics();
      drawArrow(marker, stimulus.target, signSize * 0.92, accent);
      marker.position.set(w / 2, signY);
      // Redrawn from scratch each layout, so a resize cannot stack two arrows on the sign.
      for (const child of sign.removeChildren()) child.destroy();
      sign.addChild(marker);

      for (const g of field.removeChildren()) g.destroy();
      tiles = [];
      for (let cell = 0; cell < cols * rows; cell++) {
        const g = new Graphics();
        g.eventMode = "static";
        g.cursor = "pointer";
        g.position.set(
          originX + (cell % cols) * (cellSize + gap),
          originY + Math.floor(cell / cols) * (cellSize + gap),
        );
        // Padded hit area: a mis-aimed tap here does not merely cost a point, it spends the budget
        // that ends the trial.
        const half = cellSize / 2 + cellSize * 0.1;
        g.hitArea = {
          contains: (hx: number, hy: number) =>
            hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", () => onTap(cell));
        field.addChild(g);
        tiles.push(g);
        drawTile(cell);
      }
      drawPips();
    }

    function onTap(cell: number): void {
      if (!accepting) return;
      const g = tiles[cell];
      if (!g) return;

      if (found.has(cell)) {
        // Already found. Acknowledged rather than ignored: an unanswered tap reads as broken and
        // gets tapped again, which the contamination detectors then see as a multi-touch anomaly.
        tick();
        return;
      }

      ripple(fx, g.position.x, g.position.y, accent, cellSize);

      if (targetSet.has(cell)) {
        found.add(cell);
        cellTone(cell + 2);
        drawTile(cell);
        drawPips();
        burst(fx, g.position.x, g.position.y, [accent, COLOR.correct], 10);
        void pop(g, { scale: 1.16 });
        if (found.size === targetSet.size) {
          accepting = false;
          successChime();
          void idle(520).then(() => resolveAttempt?.({ found: [...found], wrongTaps }));
        }
        return;
      }

      wrongTaps++;
      missTone();
      void nudge(g, 5);
      if (wrongTaps > budget) {
        accepting = false;
        // Reveal what was left, one at a time, so the trial teaches rather than only scoring.
        void (async () => {
          for (const missed of stimulus.targets) {
            if (found.has(missed)) continue;
            revealed.add(missed);
            drawTile(missed);
            const tile = tiles[missed];
            if (tile) {
              glow(glowLayer, tile.position.x, tile.position.y, cellSize * 0.6, COLOR.correct, 500);
            }
            cellTone(missed + 2, { volume: 0.09 });
            await idle(300);
          }
          await idle(420);
          // `found` is the child's answer and nothing else has been put in it, so it travels to the
          // record untouched.
          resolveAttempt?.({ found: [...found], wrongTaps });
        })();
      }
    }

    layout();
    app.renderer.on("resize", layout);
    tick();

    if (!prefersReducedMotion()) {
      field.children.forEach((child, i) => {
        child.scale.set(0);
        void idle(40 + i * 18).then(() => animate(240, (t) => child.scale.set(easeOutBack(t))));
      });
    }

    return {
      attempt: new Promise<Attempt>((resolve) => {
        resolveAttempt = resolve;
      }),
      /** Lets the demonstration play a tap itself. The rule is shown, never stated. */
      simulateTap: onTap,
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: SearchTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Two things are invisible here. That the circle at the top is a QUESTION rather than decoration,
   * and that the trial wants every match rather than one — a child who has played any other game in
   * this bundle has been trained by all of them to tap once and wait. So the game finds two arrows
   * itself: the sign glows, a matching tile rings, a pip fills, and then it happens again.
   *
   * Its own field under a `demo` form id, never a scored trial, and not recorded — every child sees
   * the same demonstration, so it says nothing about any of them.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateStimulus(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const shown = present(demo);
      await idle(900);
      for (const cell of demo.targets) {
        if (opts.signal?.aborted) break;
        shown.simulateTap(cell);
        await idle(620);
      }
      if (!opts.signal?.aborted) {
        await untilAbandoned(shown.attempt, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The field itself, so a trial is re-scorable from the record without this build. The
        // target direction leads, or the record would not say what the child was asked for.
        stimulus: [stimulus.target, ...stimulus.cells],
        options: [stimulus.targets],
        correctIndex: stimulus.targets.length,
      });

      const start = performance.now();
      const trial = present(stimulus);
      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus, outcome.found, outcome.wrongTaps);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        // The cells found, in the order found: how a child sweeps a field — along rows, by colour
        // clusters, or at random — is process data and is unrecoverable from the result.
        response: outcome.found,
        correct,
        responseMs: elapsed,
        // Wrong taps are the search signal here, and they are recorded as corrections for the same
        // reason spot-the-difference does it: two children can both finish while one looked and the
        // other guessed.
        corrections: outcome.wrongTaps,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.targets.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
